import { Button, Group, ScrollArea, Stack, Text, Title } from '@mantine/core';
import { modals } from '@mantine/modals';
import { useEffect, useRef, useState } from 'react';

interface VATReportDisclaimerProps {
  onAccept?: () => void;
  isLoading?: boolean;
  readOnly?: boolean;
}

export const VATReportDisclaimer: React.FC<VATReportDisclaimerProps> = ({ onAccept, isLoading = false, readOnly = false }) => {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const scrollViewportRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setTimeout(() => {
      checkIfScrolledToBottom();
    }, 100);
  }, []);

  const checkIfScrolledToBottom = () => {
    const viewport = scrollViewportRef.current;
    if (!viewport) return;

    const scrollHeight = viewport.scrollHeight;
    const scrollTop = viewport.scrollTop;
    const clientHeight = viewport.clientHeight;

    const isAtBottom = Math.abs(scrollHeight - scrollTop - clientHeight) <= 10;

    if (isAtBottom && !hasScrolledToBottom) {
      setHasScrolledToBottom(true);
    }
  };

  const handleScroll = () => {
    checkIfScrolledToBottom();
  };

  const handleClose = () => {
    modals.closeAll();
  };

  const handleAcceptClick = () => {
    if (onAccept) {
      onAccept();
      handleClose();
    }
  };

  return (
    <Stack>
      <ScrollArea h={500} onScrollPositionChange={handleScroll} viewportRef={scrollViewportRef}>
        <Stack gap="lg" pr="md">
          <section>
            <Title order={5} mb="xs" fw={600}>
              1. Nature of the Document & User responsibility
            </Title>
            <Text size="sm">
              These reports are automatically generated supporting tools designed to aid in the preparation of declarations (Intervat, Intrastat, Intracom).
              They do not constitute legal or tax advice, nor do they serve as final tax validation. These tools are provided 'as is' without explicit or
              implied warranties of completeness or absolute accuracy.{' '}
              <Text component="span" fw={600}>
                In accordance with our terms of service, you are solely responsible for reviewing and verifying the accuracy of this information before use.
              </Text>
            </Text>
          </section>

          <section>
            <Title order={5} mb="xs" fw={600}>
              2. Dependency on Customs calculation results Data & Status & Destination
            </Title>
            <Text size="sm" mb="sm">
              For Intrastat and Intracom report, but also for the Intervat report, box codes (notably 03, 46, 54, 57, 59, 71, 81, 87 for Intervat in Belgium),
              are calculated exclusively based on data extracted from the calculated results of the "
              <Text component="span" fw={600}>
                RELEASED
              </Text>
              " customs declarations.
            </Text>
            <Stack gap="xs">
              <Text size="sm">
                <Text component="span" fw={600}>
                  • Destination Uncertainty:
                </Text>
                The customs declaration does not determine the actual final destination of the goods after clearance. By default, for a Procedure 40, the
                software presumes local consumption in the Belgium (Codes 03 / 54).
              </Text>
              <Text size="sm">
                <Text component="span" fw={600}>
                  • Responsibility:
                </Text>
                If the goods are subject to a subsequent intra-community delivery (Code 46), this information must be verified and manually adjusted by the VAT
                Representative.
              </Text>
            </Stack>
          </section>

          <section>
            <Title order={5} mb="xs" fw={600}>
              3. ET14000 Authorization Setup (Specific to Intervat)
            </Title>
            <Text size="sm" mb="sm">
              The accuracy of the postponed amounts in Boxes 87 and 57 depends on the VAT profile configuration within our interface:
            </Text>
            <Stack gap="xs">
              <Text size="sm">
                <Text component="span" fw={600}>
                  • Mandatory Confirmation:
                </Text>
                The user is required to correctly specify whether the global VAT number holder possesses a valid ET14000 authorization.
              </Text>
              <Text size="sm">
                <Text component="span" fw={600}>
                  • Technical Impact:
                </Text>
                Activating this option automates postponed VAT accounting. If this box is checked in error (or if the corresponding method of payment code "G"
                is missing in the customs declaration), cash flow discrepancies and reporting errors may occur.
              </Text>
              <Text size="sm">
                <Text component="span" fw={600}>
                  • Verification:
                </Text>
                The software cannot validate the real-time status of the authorization with the tax authorities; this verification remains the sole
                responsibility of the Customs Representative.
              </Text>
            </Stack>
          </section>

          <section>
            <Title order={5} mb="xs" fw={4}>
              4. Customs Controls, Declaration Statuses, and DV614 Management (All Reports)
            </Title>
            <Text size="sm" mb="sm">
              Automated declaration processing is subject to major technical restrictions related to customs control processes:
            </Text>
            <Stack gap="xs">
              <Text size="sm">
                <Text component="span" fw={600}>
                  • Status Filter:
                </Text>
                Only declarations that have reached the "Released" status are automatically included in the reports generated by our system.
              </Text>
              <Text size="sm">
                <Text component="span" fw={600}>
                  • Exclusion of "In Control" files:
                </Text>
                When a declaration is subject to a customs control or the issuance of a DV614 document (customs fines, undervaluation, regularizations), it can
                remain frozen in the "In Control" status or updated by customs in "NOT RELEASED" status. Consequently, these declarations are automatically
                excluded from our automated reports.
              </Text>
              <Text size="sm">
                <Text component="span" fw={600}>
                  • Financial Impact and Manual Adjustment (DV614):
                </Text>
                Since DV614 documents are only transmitted by customs in PDF format, their data cannot be extracted automatically. As duties and taxes are
                collected as soon as the declaration enters the "In Control" status (with any balance due requested via the DV614), these amounts remain
                potentially deductible.{' '}
                <Text component="span" fw={600}>
                  It is the user's responsibility to manually add these regularizations
                </Text>{' '}
                to their final declarations to ensure the accuracy of the VAT base.
              </Text>
            </Stack>
          </section>

          <section>
            <Title order={5} mb="xs" fw={600}>
              5. No Tax Advice
            </Title>
            <Text size="sm">
              As a Software Provider, we do not act as a tax, customs, or accounting advisor. The calculations presented are based on standard data processing
              algorithms and regulatory presumptions. We shall not be held liable for any direct or indirect damages, including but not limited to customs
              fines, tax penalties, or financial losses arising from the use of these unverified reports.
            </Text>
          </section>

          <section>
            <Title order={5} mb="xs" fw={600}>
              6. Final Validation
            </Title>
            <Text size="sm" mb="sm">
              The Customs Representative remains solely responsible for the compliance of the final declarations submitted on administrative portals. It is
              their duty to verify overall consistency prior to submission, including:
            </Text>
            <Stack gap="xs">
              <Text size="sm">
                • The manual integration of files remaining in the "In Control" status and the adjustment of tax bases following the receipt of a DV614.
              </Text>
              <Text size="sm">• The actual logistics flows (proof of transport, final destination).</Text>
              <Text size="sm">• The validity of VAT numbers and authorizations (VIES, MyMinfin).</Text>
              <Text size="sm">• The pre-calculated amounts provided in these reports.</Text>
            </Stack>
          </section>
        </Stack>
      </ScrollArea>

      <Group justify="flex-end" mt="md">
        {readOnly ? (
          <Button variant="filled" onClick={handleClose}>
            Close
          </Button>
        ) : (
          <>
            <Button variant="outline" onClick={handleClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button
              variant="filled"
              onClick={handleAcceptClick}
              disabled={!hasScrolledToBottom}
              loading={isLoading}
              data-pw="accept-vat-report-disclaimer-button"
            >
              I have read and accept the disclaimer
            </Button>
          </>
        )}
      </Group>
    </Stack>
  );
};
